import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIENCES = [
  { id: "school", label: "School" },
  { id: "college", label: "College" },
  { id: "corporate", label: "Corporate" },
  { id: "individual", label: "Individual" },
];

/**
 * Inline brochure capture per certification SKU. Expands within the card.
 * Submits to /api/brochure-request, tagged with sku + source=brochure_gate.
 */
export default function BrochureGate({ sku, title }) {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [audience, setAudience] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Please add your name.");
    if (!/^\S+@\S+\.\S+$/.test(email)) return toast.error("Valid email required.");
    if (!audience) return toast.error("Select an audience.");
    setLoading(true);
    try {
      await axios.post(`${API}/brochure-request`, {
        name: name.trim(),
        email: email.trim(),
        audience,
        sku,
      });
      setDone(true);
      toast.success("Brochure on its way to your inbox.");
    } catch (_) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 text-[13px] link-underline"
        style={{ color: "var(--ink-secondary)" }}
        data-testid={`brochure-trigger-${sku}`}
      >
        <Download size={12} strokeWidth={1.75} />
        Download Brochure
      </button>
    );
  }

  if (done) {
    return (
      <p className="font-mono text-[11px]" style={{ color: "var(--accent-gold)", letterSpacing: "0.12em" }}>
        ✓ BROCHURE SENT
      </p>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="w-full mt-3 space-y-2 p-3 border hairline-strong rounded-sm"
      style={{ background: "var(--canvas-deep)" }}
      data-testid={`brochure-form-${sku}`}
    >
      <p
        className="font-mono text-[10px]"
        style={{ color: "var(--ink-tertiary)", letterSpacing: "0.14em" }}
      >
        DOWNLOAD · {title.toUpperCase()}
      </p>
      <input
        type="text"
        placeholder="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="form-input text-[13px] py-1.5"
        required
      />
      <input
        type="email"
        placeholder="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="form-input text-[13px] py-1.5"
        required
      />
      <div className="flex flex-wrap gap-1">
        {AUDIENCES.map((a) => (
          <button
            type="button"
            key={a.id}
            onClick={() => setAudience(a.id)}
            className={`filter-pill text-[11px] py-1 px-2.5 ${audience === a.id ? "is-active" : ""}`}
            aria-pressed={audience === a.id}
          >
            {a.label}
          </button>
        ))}
      </div>
      <div className="flex items-center justify-between gap-3">
        <button
          type="submit"
          disabled={loading}
          className="btn-primary text-[12px] px-4 py-2 disabled:opacity-60"
        >
          {loading ? "Sending…" : "Send brochure →"}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-[11px]"
          style={{ color: "var(--ink-tertiary)" }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
